// module imports
import { isSvgString } from 'modules/Helpers'
// constant imports
import CONTACTS from 'constants/Contacts'

const SERVICE_URL = '/api/images'
const WEBRTC = 'webrtc'

const { department, welcomeMenu, teamMember, guest, ringGroup, callQueues } = CONTACTS.names

/**
 * Standardize phones in contacts
 * Phone numbers that include country code and no leading +
 *
 * For entity with single phone number: phone
 * For entity with multiple numbers: phone and phones[]
 *
 * @param {[string] | string} rawPhones
 * @returns {{phone: T | string, phones: T[]}}
 */
const getPhones = (rawPhones = []) => {
  let phone
  let phones

  // single string
  if (typeof rawPhones === 'string') {
    phone = rawPhones
    // 1 item or empty array
  } else if (rawPhones.length < 2) {
    phone = rawPhones?.[0]
    // multiple phones
  } else {
    // unique set of phones
    phones = [...new Set(rawPhones.filter(Boolean))]
    phone = phones[0] // eslint-disable-line prefer-destructuring

    if (phones.length === 1) {
      phones = undefined
    }
  }

  return {
    phone,
    phones,
  }
}

const getAvatar = (avatars) =>
  avatars?.filter(
    (avatar) =>
      (!avatar.url.includes(SERVICE_URL) && // remove service generated urls
        avatar.size === '200') || // single size - our max size is 150
      isSvgString(avatar.url),
  )?.[0]?.url

export const mapperFactory = (type) => {
  switch (type) {
    case department:
      return (data) =>
        (data || []).map(({ deptId: id, lineNumber, name, extension }) => ({
          id,
          name,
          type,
          extension,
          ...getPhones(lineNumber),
        }))
    case ringGroup:
    case welcomeMenu:
      return (data) => {
        return (data || []).map(({ lines, id, name, extension }) => ({
          id,
          extension,
          name,
          type,
          ...getPhones(lines.map((line) => line.lineId)),
        }))
      }
    case callQueues:
      return (items) => {
        return (items || []).map(({ id, display_name, extension }) => ({
          id: Number(id),
          extension,
          name: display_name,
          type,
        }))
      }
    case teamMember:
    case guest:
    default:
      return (data) => {
        return (data || []).map(
          ({
            avatars,
            phone,
            id,
            unite_id: uniteId,
            name,
            email,
            extension,
            sms,
            lines = [],
            sip_credentials: sipCredentials,
          }) => ({
            id: Number.parseInt(uniteId, 10) || id,
            name: name === phone ? '' : name,
            email,
            extension,
            type,
            // TODO remove after confirming we don't use these anymore
            sipCredentials: sipCredentials?.filter(
              (credential) => credential.type === WEBRTC,
            ),
            ...getPhones([sms, phone, ...lines.map((line) => line.number)]),
            avatar: getAvatar(avatars),
          }),
        )
      }
  }
}

export const mappings = {
  // add type
  // deptId -> id
  // lineNumber: ["number"] -> phones: ["number"]
  [department]: mapperFactory(department),

  // mappings:
  // add type
  // lines: [{lineId: "number"}] -> phones: ["number"]
  [welcomeMenu]: mapperFactory(welcomeMenu),

  // mappings
  // add type
  // avatars: [{url, size}] -> url - return only one image with size 200
  // lines: [{number}] -> phones: ["number"]
  [teamMember]: mapperFactory(teamMember),
  [guest]: mapperFactory(guest),
  [ringGroup]: mapperFactory(ringGroup),
  [callQueues]: mapperFactory(callQueues),
}
